import api from './api';
import { setCookie, getCookie, deleteCookie } from '../utils/helpers';
import { ENDPOINTS } from './endpoints';
import { createClient } from '@supabase/supabase-js';

function normalizeEnv(value) {
  if (typeof value !== 'string') return value;
  let v = value.trim();
  if (
    (v.startsWith('"') && v.endsWith('"')) ||
    (v.startsWith("'") && v.endsWith("'"))
  ) {
    v = v.slice(1, -1).trim();
  }
  return v;
}

const supabaseUrl = normalizeEnv(process.env.REACT_APP_SUPABASE_URL);
const supabaseAnonKey = normalizeEnv(process.env.REACT_APP_SUPABASE_ANON_KEY);

// Supabase configuration check - warnings removed for production build
export const supabase =
  supabaseUrl && supabaseAnonKey
    ? createClient(supabaseUrl, supabaseAnonKey)
    : null;

export function getSupabaseConfigDebug() {
  return {
    url: supabaseUrl || null,
    keyPreview:
      typeof supabaseAnonKey === 'string' ? supabaseAnonKey.slice(0, 6) : null,
    configured: Boolean(supabaseUrl && supabaseAnonKey),
  };
}

function buildSignupPayload({
  fullName,
  email,
  password,
  confirmPassword,
  // gender, dateOfBirth – not sent; moved to another form later
}) {
  // const normalizedGender = ...;
  // const dob = toYyyyMmDd(dateOfBirth);
  return {
    full_name: fullName || '',
    email: email || '',
    password: password || '',
    confirm_password: confirmPassword || '',
    // gender: normalizedGender,
    // date_of_birth: dob,
  };
}

const SIGNUP_PATH = ENDPOINTS.AUTH.SIGNUP;
const LOGIN_PATH = ENDPOINTS.AUTH.LOGIN;
const GOOGLE_LOGIN_PATH = ENDPOINTS.AUTH.GOOGLE;

/**
 * Merges `{ success, message, data: { ... } }` with inner `data` so nested keys win.
 */
export function mergeAuthEnvelopeForQuestionnaire(envelope) {
  const e = envelope && typeof envelope === 'object' ? envelope : {};
  const inner = e.data && typeof e.data === 'object' ? e.data : {};
  return { ...e, ...inner };
}

/** Normalize BE questionnaire completion to 0–100 (supports 0–1 decimals). */
export function normalizeQuestionnaireCompletionPercentage(raw) {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  const pct = n > 0 && n <= 1 ? n * 100 : n;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

/**
 * Optional fields on auth responses (login / google / verify-otp) or merged envelope.
 * Supports backend typo key, correct spelling, and camelCase.
 */
export function extractLoginQuestionnaireFields(data) {
  const d = data || {};
  const medRaw =
    d.filled_medical_questionnaire ??
    d.filled_medical_questionaire ??
    d.medical_questionnaire_filled ??
    d.is_medical_questionnaire_completed ??
    d.medical_questonarie_completed ??
    d.medical_questionarie_completed ??
    d.medical_questionnaire_completed ??
    d.medicalQuestionnaireCompleted;
  let medical_questionnaire_completed = null;
  if (typeof medRaw === 'boolean') {
    medical_questionnaire_completed = medRaw;
  } else if (medRaw === 'true' || medRaw === 'false') {
    medical_questionnaire_completed = medRaw === 'true';
  } else if (medRaw === 1 || medRaw === 0 || medRaw === '1' || medRaw === '0') {
    medical_questionnaire_completed = medRaw === 1 || medRaw === '1';
  }

  let number_of_questions_filled = null;
  const nRaw = d.number_of_questions_filled ?? d.numberOfQuestionsFilled;
  if (nRaw != null && nRaw !== '') {
    const n = Number(nRaw);
    if (Number.isFinite(n)) {
      number_of_questions_filled = n;
    }
  }

  const pctRaw =
    d.questionarie_completion_percentage ??
    d.questionnaire_completion_percentage ??
    d.questionarieCompletionPercentage ??
    d.questionnaireCompletionPercentage;
  const questionnaire_completion_percentage =
    normalizeQuestionnaireCompletionPercentage(pctRaw);

  const sectionsCompletedRaw =
    d.section_completed ??
    d.sectionCompleted ??
    d.sections_completed ??
    d.sectionsCompleted;
  let sections_completed = null;
  if (sectionsCompletedRaw != null && sectionsCompletedRaw !== '') {
    const n = Number(sectionsCompletedRaw);
    if (Number.isFinite(n)) {
      sections_completed = Math.max(0, Math.round(n));
    }
  }

  const totalSectionsRaw = d.total_sections ?? d.totalSections;
  let total_sections = null;
  if (totalSectionsRaw != null && totalSectionsRaw !== '') {
    const n = Number(totalSectionsRaw);
    if (Number.isFinite(n)) {
      total_sections = Math.max(0, Math.round(n));
    }
  }

  const sectionProgressRaw = d.section_progress ?? d.sectionProgress;
  let section_progress = null;
  if (sectionProgressRaw != null && sectionProgressRaw !== '') {
    if (typeof sectionProgressRaw === 'number' || typeof sectionProgressRaw === 'string') {
      section_progress = normalizeQuestionnaireCompletionPercentage(sectionProgressRaw);
    } else   if (typeof sectionProgressRaw === 'object' && !Array.isArray(sectionProgressRaw)) {
      section_progress = normalizeQuestionnaireCompletionPercentage(
        sectionProgressRaw.percentage ??
          sectionProgressRaw.percent ??
          sectionProgressRaw.progress ??
          sectionProgressRaw.completion_percentage
      );
    }
  }

  if (section_progress == null) {
    const completedSectionsPctRaw =
      d.completed_sections_percentage ?? d.completedSectionsPercentage;
    section_progress = normalizeQuestionnaireCompletionPercentage(completedSectionsPctRaw);
  }

  return {
    medical_questionnaire_completed,
    number_of_questions_filled,
    questionnaire_completion_percentage,
    sections_completed,
    section_progress,
    total_sections,
  };
}

function gatherAuthResponseSlices(payload) {
  const root = payload && typeof payload === 'object' ? payload : {};
  const slices = [];
  const push = (o) => {
    if (o && typeof o === 'object' && !Array.isArray(o) && !slices.includes(o)) {
      slices.push(o);
    }
  };
  push(root);
  const d = root.data;
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    push(d);
    if (d.user && typeof d.user === 'object') push(d.user);
    if (d.profile && typeof d.profile === 'object') push(d.profile);
  }
  if (root.user && typeof root.user === 'object') push(root.user);
  if (root.profile && typeof root.profile === 'object') push(root.profile);
  return slices;
}

/**
 * Walk login / OAuth / verify-otp envelopes (e.g. DRF `data.user`) for questionnaire fields.
 */
export function extractLoginQuestionnaireFieldsFromEnvelope(envelope) {
  let medical_questionnaire_completed = null;
  let number_of_questions_filled = null;
  let questionnaire_completion_percentage = null;
  let sections_completed = null;
  let section_progress = null;
  let total_sections = null;
  let sawTrue = false;
  let sawFalse = false;
  for (const slice of gatherAuthResponseSlices(envelope)) {
    const q = extractLoginQuestionnaireFields(slice);
    if (typeof q.medical_questionnaire_completed === 'boolean') {
      if (q.medical_questionnaire_completed === true) sawTrue = true;
      else sawFalse = true;
    }
    if (
      number_of_questions_filled === null &&
      q.number_of_questions_filled !== null &&
      q.number_of_questions_filled !== undefined &&
      Number.isFinite(Number(q.number_of_questions_filled))
    ) {
      number_of_questions_filled = Number(q.number_of_questions_filled);
    }
    if (q.questionnaire_completion_percentage != null) {
      questionnaire_completion_percentage = q.questionnaire_completion_percentage;
    }
    if (q.sections_completed != null) {
      sections_completed = q.sections_completed;
    }
    if (q.section_progress != null) {
      section_progress = q.section_progress;
    }
    if (q.total_sections != null) {
      total_sections = q.total_sections;
    }
  }
  if (sawTrue) medical_questionnaire_completed = true;
  else if (sawFalse) medical_questionnaire_completed = false;

  return {
    medical_questionnaire_completed,
    number_of_questions_filled,
    questionnaire_completion_percentage,
    sections_completed,
    section_progress,
    total_sections,
  };
}

export function persistLoginQuestionnaireFields(q) {
  try {
    if (typeof q.medical_questionnaire_completed === 'boolean') {
      localStorage.setItem(
        'medical_questionnaire_completed',
        String(q.medical_questionnaire_completed)
      );
    }
    if (
      q.number_of_questions_filled !== null &&
      q.number_of_questions_filled !== undefined
    ) {
      localStorage.setItem(
        'number_of_questions_filled',
        String(q.number_of_questions_filled)
      );
    }
    if (q.questionnaire_completion_percentage != null) {
      localStorage.setItem(
        'questionnaire_completion_percentage',
        String(q.questionnaire_completion_percentage)
      );
    }
    if (q.sections_completed != null) {
      localStorage.setItem('section_completed', String(q.sections_completed));
      localStorage.setItem('sections_completed', String(q.sections_completed));
    }
    if (q.section_progress != null) {
      localStorage.setItem('section_progress', String(q.section_progress));
    }
    if (q.total_sections != null) {
      localStorage.setItem('total_sections', String(q.total_sections));
    }
  } catch (_) {
    // ignore localStorage errors
  }
}

export async function signUp({
  fullName,
  email,
  password,
  confirmPassword,
  // gender, dateOfBirth – not sent; moved to another form later
}) {
  const payload = buildSignupPayload({
    fullName,
    email,
    password,
    confirmPassword,
    // gender,
    // dateOfBirth,
  });
  const { data: envelope } = await api.post(SIGNUP_PATH, payload);
  const { success, message, data } = envelope || {};

  const result = {
    success: Boolean(success),
    message: message || '',
    user: data || null,
  };
  return result;
}

export default {
  signUp,
};

export async function signIn({ email, password }) {
  const payload = { email: email || '', password: password || '' };
  const { data: envelope } = await api.post(LOGIN_PATH, payload);
  const { data } = envelope || {};

  const {
    email: userEmail,
    full_name: fullName,
    date_of_birth: dateOfBirth,
    gender,
    role,
    organization,
    access,
    refresh,
    on_boarding_completed: obCompleted,
    on_boarding_step: obStep,
    must_change_password: mustChangePassword,
  } = data || {};

  if (access) setCookie('access', access, { days: 1 });
  if (refresh) setCookie('refresh', refresh, { days: 7 });

  const qFields = extractLoginQuestionnaireFieldsFromEnvelope(envelope);
  const userData = {
    email: userEmail || '',
    fullName: fullName || '',
    dateOfBirth: dateOfBirth || null,
    gender: gender || '',
    role: role || 'Patient',
    organization: organization || 'Biowell',
    on_boarding_completed: obCompleted,
    on_boarding_step: obStep != null ? String(obStep) : '',
    must_change_password: Boolean(mustChangePassword),
    medical_questionnaire_completed: qFields.medical_questionnaire_completed,
    number_of_questions_filled: qFields.number_of_questions_filled,
    questionnaire_completion_percentage: qFields.questionnaire_completion_percentage,
    sections_completed: qFields.sections_completed,
    section_progress: qFields.section_progress,
    totalSections: qFields.total_sections,
  };

  try {
    localStorage.setItem('userEmail', userData.email);
    localStorage.setItem('userFullName', userData.fullName);
    localStorage.setItem('userGender', userData.gender);
    localStorage.setItem('userRole', userData.role);
    if (userData.dateOfBirth) {
      localStorage.setItem('userDOB', userData.dateOfBirth);
    }
    localStorage.setItem('userOrganization', userData.organization);
    if (typeof obCompleted === 'boolean') {
      localStorage.setItem('on_boarding_completed', String(obCompleted));
    }
    if (obStep != null && obStep !== '') {
      localStorage.setItem('on_boarding_step', String(obStep));
    }
    localStorage.setItem('must_change_password', String(Boolean(mustChangePassword)));
    persistLoginQuestionnaireFields(qFields);
  } catch (e) {
    // ignore localStorage errors
  }

  return userData;
}

export async function googleSignIn({ idToken }) {
  const payload = { id_token: idToken };
  const { data: envelope } = await api.post(GOOGLE_LOGIN_PATH, payload);
  const { data } = envelope || {};

  const {
    email: userEmail,
    full_name: fullName,
    date_of_birth: dateOfBirth,
    gender,
    role,
    organization,
    access,
    refresh,
    on_boarding_completed: obCompleted,
    on_boarding_step: obStep,
    must_change_password: mustChangePassword,
  } = data || {};

  if (access) setCookie('access', access, { days: 1 });
  if (refresh) setCookie('refresh', refresh, { days: 7 });

  const qFields = extractLoginQuestionnaireFieldsFromEnvelope(envelope);
  const userData = {
    email: userEmail || '',
    fullName: fullName || '',
    dateOfBirth: dateOfBirth || null,
    gender: gender || '',
    role: role || 'Patient',
    organization: organization || 'Biowell',
    on_boarding_completed: obCompleted,
    on_boarding_step: obStep != null ? String(obStep) : '',
    must_change_password: Boolean(mustChangePassword),
    medical_questionnaire_completed: qFields.medical_questionnaire_completed,
    number_of_questions_filled: qFields.number_of_questions_filled,
    questionnaire_completion_percentage: qFields.questionnaire_completion_percentage,
    sections_completed: qFields.sections_completed,
    section_progress: qFields.section_progress,
    totalSections: qFields.total_sections,
  };

  try {
    localStorage.setItem('userEmail', userData.email);
    localStorage.setItem('userFullName', userData.fullName);
    localStorage.setItem('userGender', userData.gender);
    localStorage.setItem('userRole', userData.role);
    if (userData.dateOfBirth) {
      localStorage.setItem('userDOB', userData.dateOfBirth);
    }
    localStorage.setItem('userOrganization', userData.organization);
    if (typeof obCompleted === 'boolean') {
      localStorage.setItem('on_boarding_completed', String(obCompleted));
    }
    if (obStep != null && obStep !== '') {
      localStorage.setItem('on_boarding_step', String(obStep));
    }
    localStorage.setItem('must_change_password', String(Boolean(mustChangePassword)));
    persistLoginQuestionnaireFields(qFields);
  } catch (e) {
    // ignore localStorage errors
  }

  return userData;
}

export async function googleSignInWithEmail({ email, fullName }) {
  const payload = { email, full_name: fullName };
  const { data: envelope } = await api.post(GOOGLE_LOGIN_PATH, payload);
  const { data } = envelope || {};

  const {
    email: userEmail,
    full_name: fullNameResp,
    date_of_birth: dateOfBirth,
    gender,
    role,
    organization,
    access,
    refresh,
    on_boarding_completed: obCompleted,
    on_boarding_step: obStep,
    must_change_password: mustChangePassword,
  } = data || {};

  if (access) setCookie('access', access, { days: 1 });
  if (refresh) setCookie('refresh', refresh, { days: 7 });

  const qFields = extractLoginQuestionnaireFieldsFromEnvelope(envelope);
  const userData = {
    email: userEmail || '',
    fullName: fullNameResp || '',
    dateOfBirth: dateOfBirth || null,
    gender: gender || '',
    role: role || 'Patient',
    organization: organization || 'Biowell',
    on_boarding_completed: obCompleted,
    on_boarding_step: obStep != null ? String(obStep) : '',
    must_change_password: Boolean(mustChangePassword),
    medical_questionnaire_completed: qFields.medical_questionnaire_completed,
    number_of_questions_filled: qFields.number_of_questions_filled,
    questionnaire_completion_percentage: qFields.questionnaire_completion_percentage,
    sections_completed: qFields.sections_completed,
    section_progress: qFields.section_progress,
    totalSections: qFields.total_sections,
  };

  try {
    localStorage.setItem('userEmail', userData.email);
    localStorage.setItem('userFullName', userData.fullName);
    localStorage.setItem('userGender', userData.gender);
    localStorage.setItem('userRole', userData.role);
    if (userData.dateOfBirth) {
      localStorage.setItem('userDOB', userData.dateOfBirth);
    }
    localStorage.setItem('userOrganization', userData.organization);
    if (typeof obCompleted === 'boolean') {
      localStorage.setItem('on_boarding_completed', String(obCompleted));
    }
    if (obStep != null && obStep !== '') {
      localStorage.setItem('on_boarding_step', String(obStep));
    }
    localStorage.setItem('must_change_password', String(Boolean(mustChangePassword)));
    persistLoginQuestionnaireFields(qFields);
  } catch (e) {
    // ignore localStorage errors
  }

  return userData;
}

export async function signInWithFacebookSupabase(redirectTo) {
  if (!supabase) throw new Error('Supabase not configured');
  const baseUrl = process.env.REACT_APP_REDIRECT_URL || window.location.origin;
  const defaultRedirectTo = baseUrl + '/auth/callback';

  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'facebook',
    options: {
      redirectTo: redirectTo || defaultRedirectTo,
      scopes: 'email profile',
    },
  });
  if (error) throw error;
}

const GOOGLE_AUTH_FAILED_AT_KEY = 'googleAuthFailedAt';
const GOOGLE_AUTH_FAILED_TTL_MS = 5 * 60 * 1000;

export function markGoogleAuthFailed() {
  try {
    sessionStorage.setItem(GOOGLE_AUTH_FAILED_AT_KEY, String(Date.now()));
  } catch (_) {
    // ignore sessionStorage errors
  }
}

export function clearGoogleAuthFailed() {
  try {
    sessionStorage.removeItem(GOOGLE_AUTH_FAILED_AT_KEY);
  } catch (_) {
    // ignore sessionStorage errors
  }
}

export function hasRecentGoogleAuthFailure(ttlMs = GOOGLE_AUTH_FAILED_TTL_MS) {
  try {
    const raw = sessionStorage.getItem(GOOGLE_AUTH_FAILED_AT_KEY);
    const failedAt = Number(raw);
    if (!Number.isFinite(failedAt) || failedAt <= 0) return false;
    return Date.now() - failedAt < ttlMs;
  } catch (_) {
    return false;
  }
}

export async function signInWithGoogleSupabase(redirectTo) {
  if (!supabase) throw new Error('Supabase not configured');
  clearGoogleAuthFailed();
  const baseUrl = process.env.REACT_APP_REDIRECT_URL || window.location.origin;
  const defaultRedirectTo = baseUrl + '/auth/callback';

  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: redirectTo || defaultRedirectTo,
      scopes: 'email profile openid',
      queryParams: { access_type: 'offline', prompt: 'consent' },
    },
  });
  if (error) throw error;
}

export async function logout() {
  try {
    if (supabase) {
      await supabase.auth.signOut();
    }
  } catch (_) {
    // ignore signout errors
  }
  try {
    const { deleteCookie } = await import('../utils/helpers');
    deleteCookie('access');
    deleteCookie('refresh');
  } catch (_) {
    // ignore cookie deletion errors
  }
  try {
    localStorage.clear();
  } catch (_) {
    // ignore localStorage errors
  }
  try {
    sessionStorage.clear();
  } catch (_) {
    // ignore sessionStorage errors
  }
  window.location.replace('/signin');
}

let refreshInProgress = false;
export function isTokenRefreshInProgress() {
  return refreshInProgress;
}

let refreshPromise = null;
export async function refreshToken() {
  const refresh = getCookie('refresh');
  if (!refresh) throw new Error('Missing refresh token');
  if (refreshPromise) return refreshPromise;
  refreshInProgress = true;
  refreshPromise = (async () => {
    try {
      const response = await api.post(ENDPOINTS.AUTH.REFRESH, { refresh });
      const { data } = response;

      const { success, data: tokenData } = data || {};
      const { access, refresh: rotatedRefresh } = tokenData || {};

      if (!success || !access) {
        throw new Error('Token refresh failed or no access token returned');
      }

      if (access) setCookie('access', access, { days: 1 });
      if (rotatedRefresh) setCookie('refresh', rotatedRefresh, { days: 7 });
      return { access, refresh: rotatedRefresh || refresh };
    } finally {
      refreshInProgress = false;
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function getSupabaseSession() {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data?.session || null;
}

/**
 * Return the current Supabase session with a guaranteed-valid access token.
 *
 * The Supabase session is persisted in localStorage indefinitely, but its
 * access token lives ~1h. `getSession()` can hand back a session whose token
 * has already expired; forwarding that stale token to the backend is rejected
 * with 401 and traps the user in a /signin <-> /auth/callback loop. Here we
 * refresh the token when it is expired (or within `expiryLeewaySeconds` of
 * expiring) and return null when it cannot be refreshed — e.g. the refresh
 * token is also expired/revoked — so the caller can fail cleanly instead of
 * sending a token that will never verify.
 */
export async function getFreshSupabaseSession({ expiryLeewaySeconds = 60 } = {}) {
  if (!supabase) return null;

  let session = null;
  try {
    const { data } = await supabase.auth.getSession();
    session = data?.session || null;
  } catch (_) {
    return null;
  }
  if (!session) return null;

  const nowSeconds = Math.floor(Date.now() / 1000);
  const expiresAt = Number(session.expires_at) || 0;
  const stillValid = expiresAt - nowSeconds > expiryLeewaySeconds;
  if (stillValid) return session;

  // Token is expired or about to expire — force a refresh before trusting it.
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) return null;
    return data?.session || null;
  } catch (_) {
    return null;
  }
}

/**
 * Abandon a failed/stale Supabase login: drop the persisted Supabase session
 * and any partial app cookies so the user lands on a clean sign-in form rather
 * than bouncing between /signin and /auth/callback. Uses `scope: 'local'` to
 * avoid a network round-trip that would fail when the token is already invalid.
 */
export async function abandonSupabaseLogin() {
  try {
    if (supabase) {
      await supabase.auth.signOut({ scope: 'local' });
    }
  } catch (_) {
    // ignore sign-out errors
  }
  try {
    deleteCookie('access');
    deleteCookie('refresh');
  } catch (_) {
    // ignore cookie deletion errors
  }
}

export async function syncSupabaseUserToBackend(session) {
  if (!session) return null;
  const user = session.user || {};
  const payload = {
    // email/full_name kept for backwards compatibility with older backends;
    // newer backends derive both from the verified access_token.
    email: user.email || '',
    full_name: user.user_metadata?.full_name || user.user_metadata?.name || '',
    access_token: session.access_token || '',
  };
  const { data: envelope } = await api.post(GOOGLE_LOGIN_PATH, payload);
  const { data } = envelope || {};
  const { access, refresh } = data || {};
  if (access) setCookie('access', access, { days: 1 });
  if (refresh) setCookie('refresh', refresh, { days: 7 });
  const merged = mergeAuthEnvelopeForQuestionnaire(envelope || {});
  return Object.keys(merged).length ? merged : null;
}

/**
 * Verify OTP for email verification
 * @param {Object} otpData - Object containing email and otp
 * @param {string} otpData.email - User's email address
 * @param {string} otpData.otp - 6-digit OTP code
 * @returns {Promise<Object>} Response from backend
 */
export async function verifyOTP({ email, otp }) {
  try {
    const payload = {
      email: email || '',
      otp: otp || '',
    };
    const { data: envelope } = await api.post(ENDPOINTS.AUTH.VERIFY_OTP, payload);
    const { data } = envelope || {};

    const {
      email: userEmail,
      full_name: fullName,
      date_of_birth: dateOfBirth,
      gender,
      role,
      organization,
      access,
      refresh,
      on_boarding_completed: obCompleted,
      on_boarding_step: obStep,
      must_change_password: mustChangePassword,
    } = data || {};

    if (access) setCookie('access', access, { days: 1 });
    if (refresh) setCookie('refresh', refresh, { days: 7 });

    const qFields = extractLoginQuestionnaireFieldsFromEnvelope(envelope);
    const userData = {
      email: userEmail || '',
      fullName: fullName || '',
      dateOfBirth: dateOfBirth || null,
      gender: gender || '',
      role: role || 'Patient',
      organization: organization || 'Biowell',
      on_boarding_completed: obCompleted,
      on_boarding_step: obStep != null ? String(obStep) : '',
      must_change_password: Boolean(mustChangePassword),
      medical_questionnaire_completed: qFields.medical_questionnaire_completed,
      number_of_questions_filled: qFields.number_of_questions_filled,
      questionnaire_completion_percentage: qFields.questionnaire_completion_percentage,
      sections_completed: qFields.sections_completed,
      section_progress: qFields.section_progress,
      totalSections: qFields.total_sections,
    };

    try {
      localStorage.setItem('userEmail', userData.email);
      localStorage.setItem('userFullName', userData.fullName);
      localStorage.setItem('userGender', userData.gender);
      localStorage.setItem('userRole', userData.role);
      if (userData.dateOfBirth) {
        localStorage.setItem('userDOB', userData.dateOfBirth);
      }
      localStorage.setItem('userOrganization', userData.organization);
      if (typeof obCompleted === 'boolean') {
        localStorage.setItem('on_boarding_completed', String(obCompleted));
      }
      if (obStep != null && obStep !== '') {
        localStorage.setItem('on_boarding_step', String(obStep));
      }
      localStorage.setItem('must_change_password', String(Boolean(mustChangePassword)));
      persistLoginQuestionnaireFields(qFields);
    } catch (_) {
      // ignore localStorage errors (e.g., disabled or quota exceeded)
    }

    return userData;
  } catch (error) {
    const status = error.response?.status;
    const errorData = error.response?.data || {};
    
    if (status === 404) {
      return {
        success: false,
        error: errorData.error || 'No user found with the provided email address.',
      };
    }
    
    if (status === 400) {
      const errorMessage = errorData.error || '';
      
      if (errorMessage.includes('No OTP found')) {
        return {
          success: false,
          error: 'No OTP found for this user. Please request a new verification code.',
        };
      }
      
      if (errorMessage.includes('expired')) {
        return {
          success: false,
          error: 'OTP has expired. Please request a new verification code.',
        };
      }
      
      if (errorMessage.includes('Invalid OTP')) {
        return {
          success: false,
          error: 'Invalid OTP. Please check your verification code and try again.',
        };
      }
      
      return {
        success: false,
        error: errorMessage || 'Invalid OTP. Please check your verification code and try again.',
      };
    }
    
    throw error;
  }
}

/**
 * Resend OTP email to user
 * @param {Object} otpData - Object containing email
 * @param {string} otpData.email - User's email address
 * @returns {Promise<Object>} Response from backend
 */
export async function resendOTP({ email }) {
  try {
    const payload = {
      email: email || '',
    };
    const response = await api.post(ENDPOINTS.AUTH.RESEND_OTP_EMAIL, payload);
    return {
      success: true,
      message: response.data?.message || 'OTP has been resent to your email.',
    };
  } catch (error) {
    const status = error.response?.status;
    const errorData = error.response?.data || {};
    
    if (status === 404) {
      return {
        success: false,
        error: errorData.error || 'No user found with the provided email address.',
      };
    }
    
    if (status === 400) {
      return {
        success: false,
        error: errorData.error || errorData.message || 'Failed to resend OTP. Please try again.',
      };
    }
    
    throw error;
  }
}

/**
 * Send forgot password email to user
 * @param {Object} passwordData - Object containing email
 * @param {string} passwordData.email - User's email address
 * @returns {Promise<Object>} Response from backend
 */
export async function forgotPassword({ email }) {
  try {
    const payload = {
      email: email || '',
    };
    const response = await api.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, payload);
    return {
      success: true,
      message: response.data?.data?.message || response.data?.message || 'Reset link sent to your email.',
    };
  } catch (error) {
    const status = error.response?.status;
    const errorData = error.response?.data || {};
    
    if (status === 404) {
      return {
        success: false,
        error: errorData.error || 'No active user found against the provided email, check email',
      };
    }
    
    if (status === 400) {
      return {
        success: false,
        error: errorData.error || errorData.message || 'Failed to send reset link. Please try again.',
      };
    }
    
    throw error;
  }
}

/**
 * Verify reset password token
 * @param {Object} tokenData - Object containing token
 * @param {string} tokenData.token - Reset password token
 * @returns {Promise<Object>} Response from backend
 */
export async function verifyResetToken({ token }) {
  try {
    const payload = {
      token: token || '',
    };
    const response = await api.post(ENDPOINTS.AUTH.VERIFY_RESET_TOKEN, payload);
    return {
      success: true,
      message: response.data?.data?.message || response.data?.message || 'Token is valid and can be used to reset password',
    };
  } catch (error) {
    const status = error.response?.status;
    const errorData = error.response?.data || {};
    
    if (status === 400) {
      // Handle different possible error response structures
      const errorMessage = errorData.error || errorData.message || 'Invalid token provided. Please request a new reset link.';
      return {
        success: false,
        error: errorMessage,
      };
    }
    
    throw error;
  }
}

/**
 * Reset password using token
 * @param {Object} passwordData - Object containing token, password, and confirm_password
 * @param {string} passwordData.token - Reset password token
 * @param {string} passwordData.password - New password
 * @param {string} passwordData.confirm_password - Confirm new password
 * @returns {Promise<Object>} Response from backend
 */
export async function resetPassword({ token, password, confirm_password }) {
  try {
    const payload = {
      token: token || '',
      password: password || '',
      confirm_password: confirm_password || '',
    };
    const response = await api.post(ENDPOINTS.AUTH.RESET_PASSWORD, payload);
    return {
      success: true,
      message: response.data?.data?.message || response.data?.message || 'Password reset successfully',
    };
  } catch (error) {
    const status = error.response?.status;
    const errorData = error.response?.data || {};
    
    // Handle validation errors (400)
    if (status === 400) {
      const errorMessage = errorData.error || errorData.message || 'Failed to reset password. Please check your input.';
      return {
        success: false,
        error: errorMessage,
        errors: errorData.errors || {},
      };
    }
    
    // Handle other errors
    const errorMessage =
      errorData.error ||
      errorData.message ||
      error?.message ||
      'Failed to reset password. Please try again.';
    throw new Error(errorMessage);
  }
}
